import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { Heart, Trophy, Dumbbell, Timer } from 'lucide-react';
import { WodTemplate, getTemplatesByCategory } from '../../data/wodTemplates';
import { WodStructure, WodStructureUtils } from '../../types/wod.types';

interface TemplatesDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (structure: WodStructure) => void;
}

export const TemplatesDialog: React.FC<TemplatesDialogProps> = ({
  open,
  onClose,
  onSelectTemplate
}) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<WodTemplate['category']>('girl');
  const [selectedTemplate, setSelectedTemplate] = useState<WodTemplate | null>(null);

  const handleSelectTemplate = () => {
    if (selectedTemplate) {
      onSelectTemplate(selectedTemplate.structure);
      onClose();
    }
  };

  const templates = getTemplatesByCategory(selectedCategory);

  const getCategoryColor = (category: WodTemplate['category']) => {
    switch (category) {
      case 'girl': return 'bg-white/[0.06] text-muted-foreground';
      case 'hero': return 'bg-white/[0.06] text-muted-foreground';
      case 'benchmark': return 'bg-white/[0.06] text-muted-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-foreground font-bold">
            {t('wods.templatesDialog.title')}
          </DialogTitle>
        </DialogHeader>

        <Tabs
          value={selectedCategory}
          onValueChange={(value) => {
            setSelectedCategory(value as WodTemplate['category']);
            setSelectedTemplate(null);
          }}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="girl" className="flex items-center gap-2">
              <Heart className="h-4 w-4" />
              <span className="hidden sm:inline">{t('wods.templatesDialog.girlWods')}</span>
            </TabsTrigger>
            <TabsTrigger value="hero" className="flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              <span className="hidden sm:inline">{t('wods.templatesDialog.heroWods')}</span>
            </TabsTrigger>
            <TabsTrigger value="benchmark" className="flex items-center gap-2">
              <Dumbbell className="h-4 w-4" />
              <span className="hidden sm:inline">{t('wods.templatesDialog.benchmarks')}</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value={selectedCategory} className="flex-1 overflow-hidden mt-4">
            <div className="flex gap-4 h-full flex-col md:flex-row">
              {/* Liste des templates */}
              <div className="w-full md:w-2/5 overflow-y-auto space-y-2 max-h-[300px] md:max-h-none">
                {templates.map((template) => (
                  <Card
                    key={template.id}
                    onClick={() => setSelectedTemplate(template)}
                    className={`cursor-pointer transition-colors hover:border-primary ${
                      selectedTemplate?.id === template.id
                        ? 'border-primary border-2 bg-muted/50'
                        : 'border-border hover:bg-muted/30'
                    }`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-foreground font-bold text-sm">
                          {template.name}
                        </h4>
                        <Badge className={getCategoryColor(template.category)}>
                          {template.difficulty}
                        </Badge>
                      </div>

                      <p className="text-muted-foreground text-xs mb-2 line-clamp-2">
                        {template.description}
                      </p>

                      <div className="flex gap-1.5 flex-wrap">
                        <Badge variant="outline" className="text-xs gap-1">
                          <Timer className="h-3 w-3" />
                          {template.timeCap ? `${template.timeCap} min` : t('wods.templatesDialog.noCap')}
                        </Badge>
                        <Badge variant="secondary" className="text-xs text-foreground font-semibold">
                          {template.type.toUpperCase()}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Prévisualisation */}
              <div className="flex-1 overflow-y-auto">
                {selectedTemplate ? (
                  <div className="border rounded-lg p-4 bg-card h-full">
                    <h3 className="text-foreground font-bold text-lg mb-1">
                      {selectedTemplate.name}
                    </h3>

                    <p className="text-muted-foreground text-sm mb-3 italic">
                      {selectedTemplate.description}
                    </p>

                    <Separator className="mb-3" />

                    <h4 className="text-foreground font-semibold text-xs uppercase tracking-wide mb-2">
                      {t('wods.templatesDialog.wodStructure')}
                    </h4>

                    <pre className="font-mono text-sm leading-relaxed whitespace-pre-wrap bg-black/50 p-3 rounded border text-foreground overflow-auto max-h-[200px]">
                      {WodStructureUtils.generatePreview(selectedTemplate.structure)}
                    </pre>

                    <div className="mt-4 flex gap-2 flex-wrap">
                      <Badge variant="outline">
                        {t('wods.templatesDialog.movements', { count: WodStructureUtils.getTotalMovementsInStructure(selectedTemplate.structure) })}
                      </Badge>
                      {WodStructureUtils.getTotalRepsInStructure(selectedTemplate.structure) > 0 && (
                        <Badge className="bg-primary text-primary-foreground font-semibold">
                          {t('wods.templatesDialog.reps', { count: WodStructureUtils.getTotalRepsInStructure(selectedTemplate.structure) })}
                        </Badge>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="border border-dashed rounded-lg p-6 h-full flex items-center justify-center text-center">
                    <p className="text-muted-foreground">
                      {t('wods.templatesDialog.selectPrompt')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>
            {t('wods.templatesDialog.cancel')}
          </Button>
          <Button
            onClick={handleSelectTemplate}
            disabled={!selectedTemplate}
          >
            {t('wods.templatesDialog.useTemplate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
